import type { ReactNode } from "react";
import { useTranslate } from "ra-core";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Shared list-state components (guide section 6): a skeleton loading state
 * (never a spinner), an empty state, and an error state with a retry action.
 *
 * These are presentational only — the resource-aware decisions (is the
 * resource truly empty vs. did the current filters match nothing? did the
 * request error?) live in the caller (DataTable, SimpleList, List), which
 * picks the right copy/action and passes it down here.
 */

/**
 * Row placeholders for a table-based list, shown while the list request is
 * pending. Announced to assistive tech via `aria-busy` + `role="status"` and
 * a visually hidden "Cargando…" label; the placeholder bars themselves are
 * `aria-hidden` since they carry no information of their own.
 */
export const ListRowsSkeleton = ({
  rows = 5,
  columns = 4,
  className,
}: ListRowsSkeletonProps) => {
  const translate = useTranslate();
  return (
    <div
      aria-busy="true"
      role="status"
      className={cn("space-y-3 rounded-md bg-card p-4", className)}
    >
      <span className="sr-only">{translate("crm.common.loading")}</span>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          aria-hidden="true"
          className="flex items-center gap-4"
        >
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton key={columnIndex} className="h-5 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
};

export interface ListRowsSkeletonProps {
  rows?: number;
  columns?: number;
  className?: string;
}

/**
 * Card placeholders for a SimpleList (mobile) list, shown while the list
 * request is pending. Same accessible-name pattern as {@link ListRowsSkeleton}.
 */
export const ListCardsSkeleton = ({
  rows = 5,
  className,
}: ListCardsSkeletonProps) => {
  const translate = useTranslate();
  return (
    <div
      aria-busy="true"
      role="status"
      className={cn("space-y-3 p-2", className)}
    >
      <span className="sr-only">{translate("crm.common.loading")}</span>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} aria-hidden="true" className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </div>
      ))}
    </div>
  );
};

export interface ListCardsSkeletonProps {
  rows?: number;
  className?: string;
}

/**
 * Empty state: a short title, an optional description, and an optional next
 * action (e.g. a create button, or a "clear filters" button).
 */
export const ListEmptyState = ({
  title,
  description,
  action,
  className,
}: ListEmptyStateProps) => (
  <div
    className={cn(
      "flex min-h-[30vh] flex-col items-center justify-center gap-2 p-6 text-center",
      className,
    )}
  >
    <h2 className="text-lg font-semibold">{title}</h2>
    {description ? (
      <p className="text-sm text-muted-foreground">{description}</p>
    ) : null}
    {action ? <div className="mt-2">{action}</div> : null}
  </div>
);

export interface ListEmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/**
 * Error state: a short message plus a "Reintentar" action that refetches the
 * list. Uses `role="alert"` (via the shared `<Alert>` primitive).
 */
export const ListErrorState = ({
  message,
  onRetry,
  className,
}: ListErrorStateProps) => {
  const translate = useTranslate();
  return (
    <Alert variant="destructive" className={cn("text-center", className)}>
      <AlertDescription className="w-full items-center gap-2">
        <span>{message ?? translate("crm.common.list_error")}</span>
        {onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            {translate("crm.common.retry")}
          </Button>
        ) : null}
      </AlertDescription>
    </Alert>
  );
};

export interface ListErrorStateProps {
  message?: ReactNode;
  onRetry?: () => void;
  className?: string;
}
